import { Container } from "@components/wrapper"
import React from "react"
import { AiOutlineRight } from "react-icons/ai"
import Socmed from "./Socmed"
import { AppImage as Image } from "@components/misc"
import { HARISENIN_LOGO } from "@constants/const/pictures"
import { FEATURE_FLAG } from "@constants/const/feature-flag"
import Link from "next/link"
import clsx from "clsx"

interface FooterSection {
  title: string
  child: FooterLink[]
}

interface FooterLink {
  label: string
  url: string
  enabled?: boolean
}

const PROGRAM_LINKS: { label: string; url: string; enabled: boolean }[] = [
  { label: "Bootcamp", url: "/school/bootcamp", enabled: FEATURE_FLAG.bootcamp },
  { label: "ProClass", url: "/school", enabled: FEATURE_FLAG.proclass },
  { label: "Video Course", url: "/learning/category", enabled: FEATURE_FLAG.video_course },
  { label: "Event & Workshop", url: "/risebinar", enabled: FEATURE_FLAG.risebinar },
  { label: "Digital Product", url: "/digital-product", enabled: FEATURE_FLAG.digital_product },
]

const CORPORATE_LINKS: { label: string; url: string; enabled: boolean }[] = [
  {
    label: "Corporate Training",
    url: "/corporate-training",
    enabled: FEATURE_FLAG.corporate_training,
  },
  { label: "Hire Graduates", url: "/hire-graduates", enabled: FEATURE_FLAG.hire_graduates },
]

const STATIC_LINK_MENU: FooterSection[] = [
  {
    title: "About",
    child: [
      { label: "Tentang Kami", url: "/tentang-kami" },
      { label: "Alumni", url: "/alumni" },
      { label: "Komunitas", url: "/komunitas", enabled: FEATURE_FLAG.community },
      { label: "Karir", url: "https://karir.harisenin.com", enabled: FEATURE_FLAG.career },
      { label: "Blog", url: "https://tipssukses.harisenin.com", enabled: FEATURE_FLAG.blog },
    ],
  },
  {
    title: "Bantuan",
    child: [
      { label: "FAQ", url: "/faq" },
      { label: "Verifikasi Sertifikat", url: "/sertifikat" },
      { label: "Syarat & Ketentuan", url: "/syarat-ketentuan" },
      { label: "Kebijakan Privasi", url: "/kebijakan-privasi" },
      { label: "Kebijakan Garansi", url: "/kebijakan-garansi" },
    ],
  },
]

function buildLinkMenu(): FooterSection[] {
  const sections: FooterSection[] = []

  const programChild = PROGRAM_LINKS.filter((link) => link.enabled).map(({ label, url }) => ({
    label,
    url,
  }))
  if (programChild.length > 0) {
    sections.push({ title: "Program", child: programChild })
  }

  const corporateChild = CORPORATE_LINKS.filter((link) => link.enabled).map(({ label, url }) => ({
    label,
    url,
  }))
  if (corporateChild.length > 0) {
    sections.push({ title: "Corporate", child: corporateChild })
  }

  return [...sections, ...STATIC_LINK_MENU]
}

const currentYear = new Date().getFullYear()

const LINK_MENU = buildLinkMenu()

function Footer() {
  return (
    <footer className="bg-grey-f9">
      <Container className="py-6 md:py-12">
        <div className="md:flex grid md:flex-row gap-3 md:gap-16 md:justify-between sm:pb-8 pb-3">
          <div className="md:w-1/3">
            <Link href="/" passHref={true}>
              <Image
                src={HARISENIN_LOGO}
                alt="harisenin.com logo"
                className="h-6 w-auto"
                width={100}
                height={25}
              />
            </Link>
            <p className="text-xs md:text-base sm:mt-4 mt-3">
              Karena kesuksesan ialah hak setiap anak bangsa, harisenin.com berambisi untuk
              memberikan akses pendidikan yang berkualitas dan terjangkau pada generasi muda
              Indonesia.
            </p>
          </div>

          <div
            className={clsx("grid md:grid-cols-4 sm:w-fit w-full sm:gap-10 text-sm md:text-base")}
          >
            {LINK_MENU.map((value, index) => (
              <div key={index} className="sm:w-[150px] w-full">
                {/* Mobile */}
                <div className="flex flex-col my-2 md:my-0 md:hidden">
                  {/* Native <details> keeps headlessui (and react-aria) out of every page's bundle. */}
                  <details className="group">
                    <summary className="flex justify-between items-center font-urbanist text-xs font-bold w-full text-left mb-2 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                      {value.title}
                      <AiOutlineRight className="md:hidden group-open:rotate-90 transform" />
                    </summary>
                    {value.child
                      .filter((item) => item.enabled !== false)
                      .map((item, i) => (
                        <Link key={i} href={item.url} className="my-2 block text-xs">
                          {item.label}
                        </Link>
                      ))}
                  </details>
                </div>

                {/* Desktop */}
                <div className="hidden md:grid">
                  <h2 className="font-bold mb-2 font-urbanist sm:text-lg">{value.title}</h2>
                  {value.child
                    .filter((v) => v.enabled !== false)
                    .map((v, i) => (
                      <a href={v.url} key={i} className="my-1 hover:text-blue">
                        {v.label}
                      </a>
                    ))}
                </div>
              </div>
            ))}
          </div>
          <h2
            className={clsx(
              "flex justify-between items-center font-bold md:hidden mb-2 text-xs font-urbanist md:text-base"
            )}
          >
            Ikuti Kami{" "}
            <div className="sm:hidden">
              <Socmed />
            </div>
          </h2>
        </div>
        <div className="flex justify-between border-t border-grey-ec sm:pt-5 pt-3">
          <p className="sm:mt-4 text-xs md:text-base">
            © {currentYear} PT. Karya Kaya Bahagia. All rights reserved.
          </p>
          <div className="sm-only:hidden">
            <Socmed />
          </div>
        </div>
      </Container>
    </footer>
  )
}
export const HariseninFooter = () => {
  return <Footer />
}
